// Component: LiveDeploymentSettingsModal.jsx
// Purpose: Edit a RUNNING deployment's live settings (algo dashboard → deep-dive → Settings).
//   Two views: (1) edit, (2) a before→after confirmation that also states open-trade impact.
//   Money-path invariant (Deepak 2026-07-28): a settings change NEVER touches an open position —
//   it applies from the NEXT entry. The confirmation surfaces that explicitly.
//
//   The chase / profit-lock / EOD policy fields are editable + persisted via the wired PATCH
//   (services.liveExitLane.updateDeploymentSettings).
//
//   per-account-order-settings R4 (docs/pipelines/2026-07-27-per-account-order-settings/02-design.md
//   §2 Event flow R4) — per-account order type / product / quantity / validity are now REAL, editable
//   fields here (NOT read-only): GET /strategies/{strategy_id}/versions/{version_id}/allocations on
//   open populates them + captures the response's ETag (both the `ETag` response header and the
//   envelope's `data.etag`, per schemas/allocation.py::AllocationsResponseData — the body copy is
//   used preferentially since it survives axios header-casing/proxy quirks; the header is a
//   fallback). "Save changes" PUTs the full row-set back with `confirm: true` and a MANDATORY
//   `If-Match: <etag>` header (R4.1.2) — a 409 means another writer changed the rows since this
//   modal opened; that response is surfaced as "reload and retry", never silently retried with the
//   now-stale payload.
//
//   10509-15 R2/R5 — order PRICING (entry reference/side/offset + exit reference) is no longer
//   part of the allocations row above. It is now the ONE shared `OrderPricingControls` picker
//   bound to the strategy-level `/pricing` resource (getStrategyPricing/putStrategyPricing,
//   api.js) — its own GET, its own ETag, its own PUT, fired separately from the allocations PUT.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Modal, Input, Select, Badge, Skeleton } from '../../../components/ds';
import api, { services } from '../../../services/api';
import OrderPricingControls, { PlatformDefaultsChip } from '../../../components/trading/OrderPricingControls';
import { getBrokerBadge } from '../../../utils/tradingTransforms';
//! #118 WP9 (D41) — the ONE close-owner label mapping, shared with DeploymentSection.jsx.
import { eodCloseOwnerLabel, brokerNamesFromRows } from '../../../utils/closeOwner';
import {
  fetchCapabilitiesForAccounts,
  isProductAllowedForClass,
  isOrderTypeAllowedForClass,
  isTifAllowedForClass,
  getBrokerLabelForClass,
} from '../../../utils/instrumentClassCapabilityGate';

//! Field set + defaults mirror DeploymentSection's DeploymentSettingsForm (the same PATCH contract,
//  backend DeploymentPolicySettingsRequest). chase_cap_pct / profit_lock_threshold_pct are FRACTIONS.
const DEFAULT_SETTINGS = {
  chase_n: 3,
  chase_interval_s: 30,
  chase_cap_pct: 0.02,
  profit_lock_threshold_pct: '',
  eod_close_owner: 'platform_first',
};

const FIELD_LABELS = {
  chase_n: 'Chase attempts',
  chase_interval_s: 'Chase interval (s)',
  chase_cap_pct: 'Chase cap (%)',
  profit_lock_threshold_pct: 'Profit-lock (%)',
  eod_close_owner: 'EOD close owner',
};

//! per-account-order-settings QA loop-back CRIT-1 — CANONICAL order-taxonomy vocabulary, NOT
//  broker tokens. These mirror the Step-4 OrderCard's canonical option set + the backend's
//  AllocationPayload canonical validators (order_taxonomy.OrderType / .Product / DAY-IOC). The
//  per-account broker manifest (fetched below) only DISABLES the values a given account's broker
//  doesn't support + surfaces the broker_label — it never introduces a broker token here. The
//  broker adapter (order_manifest.translate_and_enforce) remains the SOLE canonical->wire
//  translator; no broker vocabulary lives on this surface.
const ORDER_TYPE_OPTIONS = ['MARKET', 'LIMIT', 'SL', 'SL-M'];
const PRODUCT_TYPE_OPTIONS = [
  { value: 'INTRADAY', label: 'Intraday' },
  { value: 'DELIVERY', label: 'Delivery' },
  { value: 'CARRYFORWARD', label: 'Carry-forward' },
  { value: 'MARGIN', label: 'Margin' },
];
const VALIDITY_OPTIONS = ['DAY', 'IOC'];

//--> Per-account capability gate built from the account's broker manifest (same source Step-4
//    uses — getBrokerCapabilities via fetchCapabilitiesForAccounts). The live modal edits a
//    RUNNING deployment and does not carry the strategy's structured instrument, so the specific
//    instrument class can't be resolved here; instead an option is treated as supported if ANY
//    class the broker serves supports it (union across classes — flattrade/icici declare an
//    identical axis set across classes, so this is exact for them). When no manifest is available
//    (fetch failed), options are NOT disabled — the server-side R7.3 re-check remains the
//    authoritative fail-closed gate on the PUT.
//
//! #28 DISPOSITION — RATIFIED FAIL-OPEN (QA loop-back 1, 2026-08-01). This is a THIRD gate-reading
//! order-authoring surface that is deliberately NOT on the shared `resolveAccountGateState` resolver
//! (which TradeExecutionStep + SmartOrderForm use). The fail-open here is SAFE and the OPPOSITE
//! polarity to issue #28, for three reasons:
//!   1. NO auto-coerce — unlike OrderCard's B6a effect, this surface never silently REWRITES a saved
//!      order_type/product_type/validity; it only enables/disables affordances. There is no
//!      write-path guard to get wrong.
//!   2. Authoritative server-side backstop — the R7.3 allocation gate
//!      (backend/.../allocation_service.py) re-checks order_type/product_type/validity against the
//!      SAME manifest FAIL-CLOSED (422) on the PUT. An option enabled under fetch-failure that the
//!      broker cannot actually place is rejected server-side, not silently sent.
//!   3. Correct failure direction — it fails OPEN (shows more options on a transient fetch blip),
//!      NOT into the #28 false-"not supported" negative (which wrongly HID supported options / auto-
//!      rewrote config). The #28 symptom therefore does not manifest on this surface.
//! Full reconciliation onto the shared resolver (a 3rd UI-surface change needing Deepak's visual
//! sign-off) is a TRACKED FOLLOW-UP, intentionally NOT done this iteration. See 03-implementation.md
//! "## Loop-back 1" Follow-ups.
const buildRowGate = (classesForBroker) => {
  const blocks = classesForBroker && typeof classesForBroker === 'object'
    ? Object.values(classesForBroker) : [];
  const anyAllows = (fn, v) => blocks.some((b) => fn(b, v));
  return {
    hasManifest: blocks.length > 0,
    productAllowed: (v) => blocks.length === 0 || anyAllows(isProductAllowedForClass, v),
    //! algo-entry-null-limit-price R5-MEDIUM fix (2026-08-12, S4) — MARKET is the PRIMARY
    //! prevention surface for a human authoring an unsupported manual MARKET order (operator
    //! decision 2a). The `blocks.length === 0 || ...` short-circuit is DELIBERATELY fail-soft for
    //! every OTHER order type (a transient fetch blip shows more options; the server-side R7.3
    //! allocation gate is the authoritative fail-closed backstop for those, per the #28
    //! DISPOSITION note above this function) — but applied to MARKET that same short-circuit
    //! PERMITS MARKET on an empty/unknown manifest, i.e. fail-OPEN for the one order type this fix
    //! exists to gate. Pin MARKET fail-CLOSED: only report it allowed when a manifest genuinely
    //! exists (`blocks.length > 0`) AND at least one served class declares it. Non-MARKET values
    //! keep the original fail-soft short-circuit unchanged.
    orderTypeAllowed: (v) => (
      v === 'MARKET'
        ? (blocks.length > 0 && anyAllows(isOrderTypeAllowedForClass, v))
        : (blocks.length === 0 || anyAllows(isOrderTypeAllowedForClass, v))
    ),
    tifAllowed: (v) => blocks.length === 0 || anyAllows(isTifAllowedForClass, v),
    getBrokerLabel: (axis, v) => {
      for (const b of blocks) {
        const lbl = getBrokerLabelForClass(b, axis, v);
        if (lbl) return lbl;
      }
      return '';
    },
  };
};
//! Per-account order-row fields diffed for the confirm-view + PUT payload (R4.1.2).
//! 10509-15 R2/R5 — price_offset/reference_type/bid_ask_side are DELETED from this set (and from
//! the allocations PUT body below): order pricing is no longer an allocation-row concern. It now
//! lives at the single `/pricing` authority (OrderPricingControls + getStrategyPricing/
//! putStrategyPricing, a SEPARATE call — see `pricingDiffs`/handleSave below), replacing the
//! algo-entry-null-limit-price §D1-3 write-path this closes.
const ALLOC_FIELDS = ['order_type', 'product_type', 'quantity', 'validity'];

//! 10509-15 R2 — the 4 canonical pricing fields, diffed/PUT the same way ALLOC_FIELDS is, but
//! against the strategy-level `/pricing` resource (keyed by account_id), never the allocations row.
const PRICING_FIELDS = ['entry_reference_type', 'entry_bid_ask_side', 'entry_price_offset', 'exit_reference_type'];

const fmtVal = (field, v) => {
  if (v === '' || v == null) return field === 'profit_lock_threshold_pct' ? 'off' : '—';
  if (field === 'chase_cap_pct') return `${(Number(v) * 100).toFixed(2)}%`;
  if (field === 'profit_lock_threshold_pct') return `${(Number(v) * 100).toFixed(0)}%`;
  if (field === 'chase_interval_s') return `${v}s`;
  //! #118 WP9 (D41) — was `'Broker RMS' : 'Platform'`. Both strings are forbidden user-facing
  //!  copy; the shared mapping is the ONE definition. No broker names are available in this
  //!  pure formatter, so the generic "your broker" fallback applies here by design — the SELECT
  //!  below, which does have the deployment's accounts, names the real broker.
  if (field === 'eod_close_owner') return eodCloseOwnerLabel(v, []);
  return String(v);
};

//--> Detect an open position for this deployment from the strategy-scoped positions feed.
//    Net non-zero qty (either sign) = a live position whose exit must match its own entry.
const findOpenPosition = (positions) => {
  if (!Array.isArray(positions)) return null;
  return positions.find((p) => {
    const q = Number(p?.net_qty ?? p?.quantity ?? p?.qty ?? 0);
    const open = p?.status ? String(p.status).toLowerCase() === 'open' : q !== 0;
    return open && q !== 0;
  }) || null;
};

export default function LiveDeploymentSettingsModal({ open, onClose, deployment, positions }) {
  const strategyId = deployment?.strategy_id;
  const deploymentId = deployment?.deployment_id;
  const versionId = deployment?.version_id;
  const isLive = deployment?.mode === 'live';

  const initialValues = useMemo(
    () => ({ ...DEFAULT_SETTINGS, ...(deployment?.policy_overrides || {}) }),
    [deployment?.policy_overrides]
  );

  const [values, setValues] = useState(initialValues);
  const [view, setView] = useState('edit');      // 'edit' | 'confirm'
  const [rows, setRows] = useState([]);           // per-account protection routing (read-only)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  //--> R4 — per-account order settings (order_type/product_type/quantity/validity), EDITABLE.
  const [allocRows, setAllocRows] = useState([]);          // current editable draft
  const [initialAllocRows, setInitialAllocRows] = useState([]); // snapshot at fetch-time, for diffing
  const [etag, setEtag] = useState(null);
  const [allocLoading, setAllocLoading] = useState(false);
  //--> account_id -> per-account canonical capability gate (manifest-driven, CRIT-1 #5).
  const [gatesByAccount, setGatesByAccount] = useState({});

  //--> 10509-15 R2/R5 — the ONE shared order-pricing authority (strategy-level, not version-scoped
  //    like `allocRows` above). `pricingRows` is keyed by account_id; `pricingDefaults` is the
  //    GET envelope's own `data.defaults` (never a hard-coded FE literal, fed straight into
  //    OrderPricingControls). A SEPARATE ETag from `etag` above — this is a different resource.
  const [pricingRows, setPricingRows] = useState([]);
  const [initialPricingRows, setInitialPricingRows] = useState([]);
  const [pricingDefaults, setPricingDefaults] = useState({});
  const [pricingEtag, setPricingEtag] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  //--> #118 WP9 (D41) — deduped broker names for this deployment's accounts, for the close-owner
  //    label. `allocRows` already carry `broker_id` per account, so this costs no extra fetch.
  const brokerNames = useMemo(() => brokerNamesFromRows(allocRows), [allocRows]);

  //--> Re-seed on (re)open or when the deployment's saved settings change.
  useEffect(() => {
    if (open) { setValues(initialValues); setView('edit'); setError(null); }
  }, [open, initialValues]);

  //--> Per-account protection routing (synthesized vs native) — server-composed, read verbatim.
  useEffect(() => {
    if (!open || !strategyId) return undefined;
    let cancelled = false;
    services.liveExitLane.getProtectionRouting(strategyId)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data;
        const data = Array.isArray(payload) ? payload : (payload?.rows ?? []);
        setRows(Array.isArray(data) ? data : []);
      })
      .catch((err) => { if (!cancelled) console.log('[DIAG] LiveSettings protection fetch', { err: err?.message }); });
    return () => { cancelled = true; };
  }, [open, strategyId]);

  //--> R4.1 — per-account order settings fetch. GET .../versions/{version_id}/allocations
  //    (R2.2, versioned surface — this modal always edits the RUNNING deployment's OWN version,
  //    never the strategy's un-versioned "current" resolution). Envelope:
  //    { status, data: { current_version_id, allocations: [...], etag, policy_overrides }, error }
  //    (backend/strategy-management/app/schemas/allocation.py::AllocationsResponseData). Goes
  //    through the gateway-routed `api` client directly (this package's file ownership does not
  //    include api.js/StrategyService for a named per-endpoint wrapper) — still gateway-only,
  //    never a direct microservice call.
  useEffect(() => {
    if (!open || !strategyId || !versionId) {
      setAllocRows([]); setInitialAllocRows([]); setEtag(null); setGatesByAccount({});
      return undefined;
    }
    let cancelled = false;
    setAllocLoading(true);
    (async () => {
      try {
        const res = await api.get(`/strategies/${strategyId}/versions/${versionId}/allocations`);
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data;
        const list = Array.isArray(payload?.allocations) ? payload.allocations : [];
        setAllocRows(list.map((r) => ({ ...r })));
        setInitialAllocRows(list.map((r) => ({ ...r })));
        //--> CRIT-1 #5 — build per-account canonical capability gates from each account's broker
        //    manifest (same getBrokerCapabilities source as Step-4). Fail-soft: on any fetch
        //    failure the account simply has no manifest -> options are not disabled and the
        //    server-side R7.3 re-check gates the PUT.
        (async () => {
          try {
            const accounts = list
              .map((r) => ({ account_id: r.account_id, broker_id: r.broker_id }))
              .filter((a) => a.account_id != null);
            const selectedIds = accounts.map((a) => a.account_id);
            //--> Defect B (#28) B3 — `fetchCapabilitiesForAccounts` now returns the additive
            //    envelope `{ manifests, status }`; unwrap `.manifests` (the unchanged
            //    broker_id -> classKey -> ClassBlock map) so this row-gate consumer is unaffected.
            const { manifests } = await fetchCapabilitiesForAccounts(accounts, selectedIds);
            if (cancelled) return;
            const gates = {};
            accounts.forEach((a) => {
              const classes = manifests?.[a.broker_id] ?? manifests?.[String(a.broker_id)] ?? null;
              gates[a.account_id] = buildRowGate(classes);
            });
            setGatesByAccount(gates);
          } catch (capErr) {
            if (!cancelled) console.log('[DIAG] LiveSettings capability fetch error', { err: capErr?.message });
          }
        })();
        //--> Prefer the envelope's own `etag` field (survives axios header-casing / proxy
        //    stripping); fall back to the response header (also set server-side, R2 gateway
        //    PROXY_PRESERVE_HEADERS gains "If-Match" — response-side ETag forwarding needs no
        //    gateway change, allocations.py:32-35).
        const headerEtag = res?.headers?.etag ?? res?.headers?.ETag;
        setEtag(payload?.etag ?? headerEtag ?? null);
      } catch (err) {
        if (cancelled) return;
        setAllocRows([]); setInitialAllocRows([]); setEtag(null); setGatesByAccount({});
        console.log('[DIAG] LiveSettings allocations fetch error', { err: err?.message, strategyId, versionId });
      } finally {
        if (!cancelled) setAllocLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, strategyId, versionId]);

  //--> 10509-15 R2 — strategy-level pricing GET (NOT version-scoped — one `/pricing` resource per
  //    strategy, shared with the Strategy Builder's own panel). Fetches via the api.js service
  //    layer (getStrategyPricing), gateway-routed only.
  useEffect(() => {
    if (!open || !strategyId) {
      setPricingRows([]); setInitialPricingRows([]); setPricingDefaults({}); setPricingEtag(null);
      return undefined;
    }
    let cancelled = false;
    setPricingLoading(true);
    (async () => {
      try {
        const res = await services.strategies.getStrategyPricing(strategyId);
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data;
        const list = Array.isArray(payload?.pricing) ? payload.pricing : [];
        setPricingRows(list.map((r) => ({ ...r })));
        setInitialPricingRows(list.map((r) => ({ ...r })));
        setPricingDefaults(payload?.defaults || {});
        const headerEtag = res?.headers?.etag ?? res?.headers?.ETag;
        setPricingEtag(payload?.etag ?? headerEtag ?? null);
      } catch (err) {
        if (cancelled) return;
        setPricingRows([]); setInitialPricingRows([]); setPricingDefaults({}); setPricingEtag(null);
        console.log('[DIAG] LiveSettings pricing fetch error', { err: err?.message, strategyId });
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, strategyId]);

  const handlePricingChange = useCallback((accountId, nextRow) => {
    setPricingRows((prev) => {
      const exists = prev.some((r) => r.account_id === accountId);
      if (exists) return prev.map((r) => (r.account_id === accountId ? { ...r, ...nextRow } : r));
      return [...prev, { ...nextRow, account_id: accountId }];
    });
  }, []);

  const setField = useCallback((field) => (e) => {
    const raw = e?.target ? e.target.value : e;
    setValues((prev) => ({ ...prev, [field]: raw }));
  }, []);

  const handleAllocFieldChange = useCallback((accountId, field) => (e) => {
    const raw = e?.target ? e.target.value : e;
    const value = field === 'quantity' ? (raw === '' ? '' : parseInt(raw, 10)) : raw;
    setAllocRows((prev) => prev.map((r) => (r.account_id === accountId ? { ...r, [field]: value } : r)));
  }, []);

  //--> Only the fields that actually changed, for the before→after confirmation grid.
  const diffs = useMemo(() => {
    return Object.keys(FIELD_LABELS)
      .filter((f) => String(values[f] ?? '') !== String(initialValues[f] ?? ''))
      .map((f) => ({ field: f, label: FIELD_LABELS[f], from: initialValues[f], to: values[f] }));
  }, [values, initialValues]);

  //--> R4.1.2 — per-account order-row diffs (account_id + field granularity), for both the
  //    confirm-view diff grid and gating whether the allocations PUT fires at all.
  const allocDiffs = useMemo(() => {
    const initialByAccount = new Map(initialAllocRows.map((r) => [r.account_id, r]));
    const out = [];
    allocRows.forEach((row) => {
      const before = initialByAccount.get(row.account_id);
      if (!before) return;
      ALLOC_FIELDS.forEach((f) => {
        if (String(row[f] ?? '') !== String(before[f] ?? '')) {
          out.push({ account_id: row.account_id, field: f, from: before[f], to: row[f] });
        }
      });
    });
    return out;
  }, [allocRows, initialAllocRows]);

  //--> 10509-15 R2 — per-account PRICING diffs, same account_id+field granularity as allocDiffs,
  //    but against the separate `/pricing` resource (never the allocations PUT body).
  const pricingDiffs = useMemo(() => {
    const initialByAccount = new Map(initialPricingRows.map((r) => [r.account_id, r]));
    const out = [];
    pricingRows.forEach((row) => {
      const before = initialByAccount.get(row.account_id);
      if (!before) return;
      PRICING_FIELDS.forEach((f) => {
        if (String(row[f] ?? '') !== String(before[f] ?? '')) {
          out.push({ account_id: row.account_id, field: f, from: before[f], to: row[f] });
        }
      });
    });
    return out;
  }, [pricingRows, initialPricingRows]);

  const openPosition = useMemo(() => findOpenPosition(positions), [positions]);

  const handleSave = useCallback(async () => {
    if (!strategyId || !deploymentId) return;
    setSaving(true); setError(null);
    try {
      //--> Exit-execution policy PATCH — unchanged contract/behavior (R7.2).
      await services.liveExitLane.updateDeploymentSettings(strategyId, deploymentId, values);

      //--> R4.1.2 — per-account order settings PUT. MANDATORY If-Match on this call site (unlike
      //    Step-4's first-save case, allocation_service.py:395-399's missing-If-Match tolerance is
      //    NOT relied on here — a live-edit without a fresh ETag is refused, fail-closed, rather
      //    than risking a silent stomp of a concurrent writer's change).
      if (allocDiffs.length > 0) {
        if (!versionId) {
          setError('Could not identify the running version — reload and retry.');
          setSaving(false);
          return;
        }
        if (!etag) {
          setError('Could not verify current settings — reload and retry.');
          setSaving(false);
          return;
        }
        //--> 10509-15 R2/R5 — pricing fields are GONE from this payload; they live at the
        //    separate `/pricing` resource below (its own ETag-gated PUT, its own call).
        const allocationsPayload = allocRows.map((r) => ({
          account_id: r.account_id,
          quantity: Number(r.quantity),
          order_type: r.order_type,
          product_type: r.product_type,
          validity: r.validity,
        }));
        await api.put(
          `/strategies/${strategyId}/versions/${versionId}/allocations`,
          { allocations: allocationsPayload, confirm: true },
          { headers: { 'If-Match': etag } },
        );
      }

      //--> 10509-15 R2 — per-account pricing PUT. A SEPARATE call from the allocations PUT above
      //    (different resource, different ETag). Same fail-closed ETag discipline as R4.1.2.
      if (pricingDiffs.length > 0) {
        if (!pricingEtag) {
          setError('Could not verify current pricing — reload and retry.');
          setSaving(false);
          return;
        }
        const pricingPayload = pricingRows.map((r) => ({
          account_id: r.account_id,
          entry_reference_type: r.entry_reference_type,
          entry_bid_ask_side: r.entry_bid_ask_side,
          entry_price_offset: r.entry_price_offset,
          exit_reference_type: r.exit_reference_type,
        }));
        await services.strategies.putStrategyPricing(strategyId, pricingPayload, pricingEtag);
      }

      onClose?.({ saved: true });
    } catch (err) {
      if (err?.response?.status === 409 || err?.response?.status === 412) {
        //--> R4.1.2 / 10509-15 R2 — do NOT retry with the now-stale payload; the operator must
        //    reload to see the current rows + a fresh ETag (409 = allocations, 412 = pricing).
        setError('Settings changed elsewhere — reload and retry.');
      } else {
        setError('Could not save — try again.');
      }
      console.log('[DIAG] LiveSettings save error', { err: err?.message });
    } finally {
      setSaving(false);
    }
  }, [strategyId, deploymentId, versionId, values, allocDiffs, allocRows, etag, pricingDiffs, pricingRows, pricingEtag, onClose]);

  if (!open) return null;

  const reviewDisabled = diffs.length === 0 && allocDiffs.length === 0 && pricingDiffs.length === 0;

  const editFooter = (
    <div className="flex items-center justify-between w-full gap-3">
      <span className="text-[11px] text-fg-3">Applies from the next entry — open positions are unaffected.</span>
      <div className="flex items-center gap-2">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onClose?.()}>Cancel</button>
        <button type="button" className="btn btn-primary btn-sm" disabled={reviewDisabled}
          onClick={() => setView('confirm')} title={reviewDisabled ? 'No changes to review' : undefined}>
          Review changes
        </button>
      </div>
    </div>
  );

  const confirmFooter = (
    <div className="flex items-center justify-end w-full gap-2">
      {error && <span className="text-xs text-loss mr-auto">{error}</span>}
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setView('edit')} disabled={saving}>Back</button>
      <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Confirm & save'}
      </button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={() => onClose?.()}
      size="md"
      title={view === 'edit' ? 'Live settings' : 'Confirm live changes'}
      footer={view === 'edit' ? editFooter : confirmFooter}
    >
      {view === 'edit' ? (
        <div className="space-y-4">
          {/* Per-account protection (read-only, server-composed) */}
          <div className="space-y-2">
            <h5 className="text-[11px] font-bold text-fg-3 uppercase tracking-wider">Per-account exit protection</h5>
            {rows.length === 0 ? (
              <p className="text-xs text-fg-3">No allocated accounts yet.</p>
            ) : (
              <div className="space-y-1.5">
                {rows.map((row) => {
                  const b = getBrokerBadge(row.broker);
                  const native = row.mechanism === 'native';
                  return (
                    <div key={row.account_id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-bg-3 border border-[var(--divider)] text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        {b ? <Badge variant={b.variant} size="sm">{b.label}</Badge> : <span className="text-fg-2">—</span>}
                        <span className="font-mono text-fg-2 truncate" title={row.account_id}>{row.account_id}</span>
                      </div>
                      <span className={native ? 'text-[var(--badge-profit-fg)]' : 'text-[var(--badge-info-fg)]'}>{row.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* R4 — per-account order settings, EDITABLE (order_type / product_type / quantity /
              validity) — no longer read-only; persists via the allocations PUT (If-Match-gated). */}
          <div className="space-y-2">
            <h5 className="text-[11px] font-bold text-fg-3 uppercase tracking-wider">Per-account order settings</h5>
            {allocLoading ? (
              <p className="text-xs text-fg-3">Loading…</p>
            ) : allocRows.length === 0 ? (
              <p className="text-xs text-fg-3">No allocated accounts yet.</p>
            ) : (
              <div className="space-y-2">
                {allocRows.map((row) => {
                  const gate = gatesByAccount[row.account_id];
                  //! algo-entry-null-limit-price R5-MEDIUM (S4) — the original `gated` short-
                  //! circuited to `allowed=true` whenever `!gate?.hasManifest` (no manifest fetched
                  //! yet, OR `gate` itself still undefined pre-fetch) WITHOUT EVER CALLING
                  //! `allowedFn` — so even with `buildRowGate`'s `orderTypeAllowed` now pinned
                  //! fail-closed for MARKET, this wrapper still bypassed it. MARKET is special-
                  //! cased to always consult `allowedFn` (which itself now fails closed on no
                  //! manifest); every other axis/value keeps the original fail-soft short-circuit.
                  const gated = (allowedFn, v) => (
                    v === 'MARKET'
                      ? (typeof allowedFn === 'function' && allowedFn(v))
                      : (!gate?.hasManifest || (typeof allowedFn === 'function' && allowedFn(v)))
                  );
                  const brokerLabel = gate?.getBrokerLabel?.('products', row.product_type) || '';
                  return (
                  <div key={row.account_id} className="p-2.5 rounded-lg bg-bg-3 border border-[var(--divider)] space-y-2">
                    <span className="font-mono text-[11px] text-fg-2 truncate block" title={row.account_id}>{row.account_id}</span>
                    <div className="grid grid-cols-4 gap-1.5">
                      <Select size="sm" label="Order" value={row.order_type} onChange={handleAllocFieldChange(row.account_id, 'order_type')}>
                        {/*! S4 — MARKET is HIDDEN outright (filtered out of the option list) when
                            the gate reports it not-allowed/unknown, never merely disabled-and-
                            visible like the other order types below (which keep the pre-existing
                            fail-soft "— not supported" disabled-option treatment; the server-side
                            R7.3 allocation gate remains their authoritative fail-closed backstop). */}
                        {ORDER_TYPE_OPTIONS
                          .filter((o) => o !== 'MARKET' || gated(gate?.orderTypeAllowed, o))
                          .map((o) => {
                          const allowed = gated(gate?.orderTypeAllowed, o);
                          return <option key={o} value={o} disabled={!allowed}>{allowed ? o : `${o} — not supported`}</option>;
                        })}
                      </Select>
                      <Select size="sm" label="Product" value={row.product_type} onChange={handleAllocFieldChange(row.account_id, 'product_type')}>
                        {PRODUCT_TYPE_OPTIONS.map((o) => {
                          const allowed = gated(gate?.productAllowed, o.value);
                          return <option key={o.value} value={o.value} disabled={!allowed}>{allowed ? o.label : `${o.label} — not supported`}</option>;
                        })}
                      </Select>
                      <Input size="sm" label="Qty" type="number" min={1} value={row.quantity} onChange={handleAllocFieldChange(row.account_id, 'quantity')} />
                      <Select size="sm" label="Validity" value={row.validity} onChange={handleAllocFieldChange(row.account_id, 'validity')}>
                        {VALIDITY_OPTIONS.map((o) => {
                          const allowed = gated(gate?.tifAllowed, o);
                          return <option key={o} value={o} disabled={!allowed}>{allowed ? o : `${o} — not supported`}</option>;
                        })}
                      </Select>
                    </div>
                    {brokerLabel && (
                      <p className="text-[10px] text-fg-3">Broker calls this product &quot;{brokerLabel}&quot;</p>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 10509-15 R2/R5 — per-account order PRICING, now the ONE shared OrderPricingControls
              picker (replaces the inline reference/side/offset <Select>s this block used to own).
              Separate resource/ETag/PUT from the allocations section above — its own GET/PUT via
              getStrategyPricing/putStrategyPricing (api.js), strategy-level (not version-scoped). */}
          <div className="space-y-2">
            <h5 className="text-[11px] font-bold text-fg-3 uppercase tracking-wider">Per-account order pricing</h5>
            {pricingLoading ? (
              <div className="p-2.5 rounded-lg bg-bg-3 border border-[var(--divider)] space-y-2">
                <Skeleton w="35%" h={11} />
                <Skeleton h={32} />
                <Skeleton w="60%" h={10} />
              </div>
            ) : allocRows.length === 0 ? (
              <p className="text-xs text-fg-3">No allocated accounts yet.</p>
            ) : (
              <div className="space-y-2">
                {allocRows.map((row) => {
                  const pricingRow = pricingRows.find((r) => r.account_id === row.account_id) || {};
                  return (
                    <div key={row.account_id} className="p-2.5 rounded-lg bg-bg-3 border border-[var(--divider)] space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[11px] text-fg-2 truncate" title={row.account_id}>{row.account_id}</span>
                        {/*! Iteration 4 — ONE combined chip per account row, replacing the two
                            per-section "Platform default" pills OrderPricingControls used to render
                            internally. */}
                        <PlatformDefaultsChip value={pricingRow} defaults={pricingDefaults} />
                      </div>
                      <OrderPricingControls
                        value={pricingRow}
                        defaults={pricingDefaults}
                        onChange={(next) => handlePricingChange(row.account_id, next)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Editable exit-execution policy (wired PATCH) */}
          <div className="space-y-2.5">
            <h5 className="text-[11px] font-bold text-fg-3 uppercase tracking-wider">Exit-execution policy</h5>
            <div className="grid grid-cols-2 gap-2">
              <Input size="sm" label="Chase attempts (N)" type="number" min={0} value={values.chase_n} onChange={setField('chase_n')} />
              <Input size="sm" label="Chase interval (s)" type="number" min={1} value={values.chase_interval_s} onChange={setField('chase_interval_s')} />
              <Input size="sm" label="Chase cap (fraction)" type="number" min={0} max={1} step="0.01" value={values.chase_cap_pct} onChange={setField('chase_cap_pct')} />
              <Input size="sm" label="Profit-lock (fraction)" type="number" min={0} max={1} step="0.01" value={values.profit_lock_threshold_pct} onChange={setField('profit_lock_threshold_pct')} />
            </div>
            {/*! #118 WP9 (D41) — the option copy now says exactly WHO closes the position, with
                the real broker resolved from this deployment's own accounts (`allocRows` already
                carry `broker_id`, so no extra fetch). Stored enum values are unchanged. */}
            <Select size="sm" label="EOD close owner" value={values.eod_close_owner} onChange={setField('eod_close_owner')}>
              <option value="broker_rms">{eodCloseOwnerLabel('broker_rms', brokerNames)}</option>
              <option value="platform_first">{eodCloseOwnerLabel('platform_first', brokerNames)}</option>
            </Select>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Open-trade impact — the money-path invariant, surfaced */}
          {openPosition ? (
            <div className="p-3 rounded-lg bg-[var(--badge-warn-bg)] border border-[color-mix(in_oklch,var(--color-loss)_25%,transparent)]">
              <div className="text-[13px] font-semibold text-[var(--badge-warn-fg)]">Open position — applies from the next entry</div>
              <div className="text-xs text-fg-2 mt-1">
                {(openPosition.side || openPosition.direction || 'Position')}{' '}
                {Number(openPosition.net_qty ?? openPosition.quantity ?? openPosition.qty)} open.
                This position keeps its current settings through exit; your changes take effect on the <b>next entry</b> only.
              </div>
            </div>
          ) : (
            <div className="p-3 rounded-lg bg-[var(--badge-info-bg)] border border-[color-mix(in_oklch,var(--badge-info-dot)_25%,transparent)]">
              <div className="text-[13px] font-semibold text-[var(--badge-info-fg)]">No open position</div>
              <div className="text-xs text-fg-2 mt-1">Your changes take effect on the next entry.</div>
            </div>
          )}

          {/* Before → after grid — exit-execution policy */}
          {diffs.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-[11px] font-bold text-fg-3 uppercase tracking-wider">You’re changing</h5>
              <div className="space-y-1.5">
                {diffs.map((d) => (
                  <div key={d.field} className="grid grid-cols-[1fr_auto] items-center gap-2 p-2 rounded-lg bg-bg-3 border border-[var(--divider)]">
                    <span className="text-xs text-fg-2">{d.label}</span>
                    <span className="text-xs font-mono flex items-center gap-2">
                      <span className="text-fg-3 line-through">{fmtVal(d.field, d.from)}</span>
                      <span className="text-fg-3">→</span>
                      <span className="text-[var(--badge-profit-fg)] font-bold">{fmtVal(d.field, d.to)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Before → after grid — per-account order settings (R4.1.2) */}
          {allocDiffs.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-[11px] font-bold text-fg-3 uppercase tracking-wider">Per-account order settings changes</h5>
              <div className="space-y-1.5">
                {allocDiffs.map((d) => (
                  <div key={`${d.account_id}-${d.field}`} className="grid grid-cols-[1fr_auto] items-center gap-2 p-2 rounded-lg bg-bg-3 border border-[var(--divider)]">
                    <span className="text-xs text-fg-2 font-mono truncate" title={d.account_id}>{d.account_id} · {d.field}</span>
                    <span className="text-xs font-mono flex items-center gap-2">
                      <span className="text-fg-3 line-through">{String(d.from ?? '—')}</span>
                      <span className="text-fg-3">→</span>
                      <span className="text-[var(--badge-profit-fg)] font-bold">{String(d.to ?? '—')}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Before → after grid — per-account pricing (10509-15 R2) */}
          {pricingDiffs.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-[11px] font-bold text-fg-3 uppercase tracking-wider">Per-account pricing changes</h5>
              <div className="space-y-1.5">
                {pricingDiffs.map((d) => (
                  <div key={`${d.account_id}-${d.field}`} className="grid grid-cols-[1fr_auto] items-center gap-2 p-2 rounded-lg bg-bg-3 border border-[var(--divider)]">
                    <span className="text-xs text-fg-2 font-mono truncate" title={d.account_id}>{d.account_id} · {d.field}</span>
                    <span className="text-xs font-mono flex items-center gap-2">
                      <span className="text-fg-3 line-through">{String(d.from ?? '—')}</span>
                      <span className="text-fg-3">→</span>
                      <span className="text-[var(--badge-profit-fg)] font-bold">{String(d.to ?? '—')}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isLive && (
            <p className="text-[11px] text-fg-3">This is a <b>live</b> deployment — changes are audited.</p>
          )}
        </div>
      )}
    </Modal>
  );
}
